import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Link,
} from "@mui/material";
import {
  Close,
  EventRepeat,
  CheckCircle,
  CalendarMonth,
  Replay,
  ArrowBack,
  Check,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { pluralise } from "../../../../helpers/text";
import HelpLink from "../../../help/HelpLink";
import { Link as RouterLink } from "react-router-dom";

const RECURRENCE_TYPES = [
  {
    id: "fixed_schedule",
    label: "Recur on a specific day",
    description:
      "Task is added back to your list on the same day each week/month",
    icon: CalendarMonth,
    exampleCase: "Take bins out",
    exampleSolution: "Every Monday",
  },
  {
    id: "after_completion",
    label: "Recur after completion",
    description: "Countdown starts after you complete the task",
    icon: Replay,
    exampleCase: "Clean bathroom",
    exampleSolution: "2 weeks after you finish it",
  },
];

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const WEEKDAY_OCCURRENCES = [
  { value: 1, label: "First" },
  { value: 2, label: "Second" },
  { value: 3, label: "Third" },
  { value: 4, label: "Fourth" },
  { value: -1, label: "Last" },
];

const RecurrenceDialog = ({
  open,
  onClose,
  frequency,
  setFrequency,
  onSave,
}) => {
  const [step, setStep] = useState(0);
  const [recurrenceType, setRecurrenceType] = useState(
    frequency?.startIntervalOnCompletion
      ? "after_completion"
      : "fixed_schedule",
  );
  const [intervalCount, setIntervalCount] = useState(
    frequency?.intervalCount || 1,
  );
  const [intervalDateType, setIntervalDateType] = useState(
    frequency?.intervalDateType || "week",
  );
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(dayjs().day());
  const [selectedDate, setSelectedDate] = useState(dayjs()); // For month/year recurrence
  const [intervalCountError, setIntervalCountError] = useState("");
  const [monthScheduleMode, setMonthScheduleMode] = useState("date"); // "date" | "weekday"
  const [weekdayOccurrence, setWeekdayOccurrence] = useState(1); // 1-4 or -1 for "last"
  const [selectedMonthWeekday, setSelectedMonthWeekday] = useState(
    dayjs().day(),
  );

  const steps = ["Choose type", "Configure"];

  const handleTypeSelect = (typeId) => {
    setRecurrenceType(typeId);
    setStep(1);
  };

  const handleBack = () => {
    if (step === 0) {
      onClose();
    } else {
      setStep(step - 1);
    }
  };

  // Helper to find Nth weekday occurrence in a month
  const findNthWeekdayInMonth = (month, n, dow) => {
    if (n === -1) {
      // "Last" occurrence - find from end of month
      let date = month.endOf("month");
      while (date.day() !== dow) {
        date = date.subtract(1, "day");
      }
      return date.startOf("day");
    }

    // Find first occurrence of this weekday in the month
    let date = month.startOf("month");
    while (date.day() !== dow) {
      date = date.add(1, "day");
    }
    // Add (n-1) weeks
    return date.add(n - 1, "week").startOf("day");
  };

  const calculateNextNthWeekday = (occurrence, dayOfWeek) => {
    const today = dayjs();

    // Check this month first
    const thisMonthTarget = findNthWeekdayInMonth(today, occurrence, dayOfWeek);
    if (
      thisMonthTarget.isAfter(today) ||
      thisMonthTarget.isSame(today, "day")
    ) {
      return thisMonthTarget.valueOf();
    }

    // Otherwise, next month
    return findNthWeekdayInMonth(
      today.add(1, "month"),
      occurrence,
      dayOfWeek,
    ).valueOf();
  };

  const handleSave = () => {
    // Validate intervalCount before saving
    if (!intervalCount || intervalCount < 1) {
      setIntervalCountError("Must be at least 1");
      return;
    }

    let newFrequency;

    if (recurrenceType === "after_completion") {
      newFrequency = {
        startDate: dayjs().startOf("day").valueOf(),
        intervalCount,
        intervalDateType,
        startIntervalOnCompletion: true,
      };
    } else {
      // Fixed schedule - calculate the next occurrence of the selected day
      let startDate;
      if (intervalDateType === "week") {
        // Find the next occurrence of the selected day of week
        const today = dayjs();
        const targetDay = selectedDayOfWeek;
        const currentDay = today.day();
        const daysUntilTarget = (targetDay - currentDay + 7) % 7 || 7;
        startDate = today.add(daysUntilTarget, "day").startOf("day").valueOf();
      } else if (intervalDateType === "month") {
        if (monthScheduleMode === "weekday") {
          // Nth weekday of month (e.g., "2nd Monday of every month")
          startDate = calculateNextNthWeekday(
            weekdayOccurrence,
            selectedMonthWeekday,
          );
        } else {
          // Specific date of month (existing logic)
          const today = dayjs();
          const targetDay = selectedDate.date();

          // Helper function to get the valid day for a given month
          const getValidDay = (date) => {
            const daysInMonth = date.daysInMonth();
            return Math.min(targetDay, daysInMonth);
          };

          // For monthly: just use the day of month, find next occurrence
          const currentDay = today.date();
          if (targetDay > currentDay) {
            // Same month
            const validDay = getValidDay(today);
            startDate = today.date(validDay).startOf("day").valueOf();
          } else {
            // Next month
            const nextMonth = today.add(1, "month");
            startDate = nextMonth
              .date(getValidDay(nextMonth))
              .startOf("day")
              .valueOf();
          }
        }
      } else {
        // Daily tasks - start tomorrow
        startDate = dayjs().add(1, "day").startOf("day").valueOf();
      }

      newFrequency = {
        startDate,
        intervalCount,
        intervalDateType,
        startIntervalOnCompletion: false,
      };

      // For monthly tasks, store additional data based on mode
      if (intervalDateType === "month") {
        if (monthScheduleMode === "weekday") {
          newFrequency.additionalData = {
            dayOfWeek: selectedMonthWeekday,
            timesOccurred: weekdayOccurrence,
          };
        } else {
          newFrequency.additionalData = {
            intendedDayOfMonth: selectedDate.date(),
          };
        }
      }
    }

    setFrequency(newFrequency);
    if (onSave) onSave(newFrequency);
    onClose();
  };

  const summaryText = useMemo(() => {
    if (recurrenceType === "after_completion") {
      return `${intervalCount} ${pluralise(intervalDateType, intervalCount)} after completion`;
    }
    if (intervalDateType === "week") {
      const dayName = DAYS_OF_WEEK.find(
        (d) => d.value === selectedDayOfWeek,
      )?.label;
      return `Every ${intervalCount > 1 ? intervalCount + " " : ""}${pluralise(intervalDateType, intervalCount)} on ${dayName}`;
    }
    if (intervalDateType === "month") {
      if (monthScheduleMode === "weekday") {
        const ordinal = WEEKDAY_OCCURRENCES.find(
          (o) => o.value === weekdayOccurrence,
        )?.label;
        const dayName = DAYS_OF_WEEK.find(
          (d) => d.value === selectedMonthWeekday,
        )?.label;
        return `Every ${intervalCount > 1 ? intervalCount + " " : ""}${pluralise(intervalDateType, intervalCount)} on the ${ordinal} ${dayName}`;
      }
      const day = selectedDate.date();
      const suffix =
        day === 1 ? "st" : day === 2 ? "nd" : day === 3 ? "rd" : "th";
      return `Every ${intervalCount > 1 ? intervalCount + " " : ""}${pluralise(intervalDateType, intervalCount)} on the ${day}${suffix}`;
    }
    return `Every ${intervalCount > 1 ? intervalCount + " " : ""}${pluralise(intervalDateType, intervalCount)}`;
  }, [
    recurrenceType,
    intervalCount,
    intervalDateType,
    selectedDayOfWeek,
    selectedDate,
    monthScheduleMode,
    weekdayOccurrence,
    selectedMonthWeekday,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1301 }} // Nested — above parent dialogs (MUI default 1300)
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          bgcolor: "#004a3c",
          color: "white !important",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EventRepeat sx={{ color: "white !important" }} />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ color: "white !important" }}
          >
            Set Recurrence
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <HelpLink to="/about/features/tasks/recurring-tasks" color="white" />
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{ color: "white !important" }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Stepper activeStep={step} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent
        className="scroller"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        {step === 0 && (
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mb: 3, textAlign: "center" }}
            >
              How often should this task recur?
            </Typography>
            <List sx={{ py: 0 }}>
              {RECURRENCE_TYPES.map((type, index) => {
                const Icon = type.icon;
                const isSelected = recurrenceType === type.id;
                const isFirst = index === 0;

                return (
                  <ListItem key={type.id} disablePadding sx={{ mb: 1.5 }}>
                    <ListItemButton
                      onClick={() => handleTypeSelect(type.id)}
                      sx={{
                        py: 2.5,
                        px: 2,
                        borderRadius: 2,
                        border: "2px solid",
                        borderColor: isSelected ? "#197b68" : "grey.300",
                        bgcolor: isSelected
                          ? "rgba(25, 123, 104, 0.08)"
                          : "transparent",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: isSelected
                            ? "rgba(25, 123, 104, 0.12)"
                            : "rgba(0, 0, 0, 0.04)",
                          borderColor: "#197b68",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 56 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            bgcolor:
                              isSelected || isFirst ? "#197b68" : "grey.200",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <Icon
                            sx={{
                              color:
                                isSelected || isFirst ? "white" : "grey.600",
                            }}
                          />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
                            {type.label}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {type.description}
                            </Typography>
                            <Box
                              sx={{
                                mt: 1.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                Example:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 500 }}
                              >
                                "{type.exampleCase}"
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                →
                              </Typography>
                              <Chip
                                label={type.exampleSolution}
                                size="small"
                                sx={{
                                  bgcolor: "grey.100",
                                  color: "grey.700",
                                  fontSize: "0.7rem",
                                  height: 20,
                                }}
                              />
                            </Box>
                          </Box>
                        }
                      />
                      {isSelected && <Check sx={{ color: "#197b68", ml: 1 }} />}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ flex: 1 }}>
            {/* Current selection indicator */}
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                bgcolor: "#004a3c",
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {recurrenceType === "after_completion" ? (
                <Replay sx={{ color: "white !important" }} />
              ) : (
                <CalendarMonth sx={{ color: "white !important" }} />
              )}
              <Typography
                variant="body1"
                fontWeight="medium"
                sx={{ color: "white !important", mb: "0 !important" }}
              >
                {recurrenceType === "after_completion"
                  ? "Recur after completion"
                  : "Recur on a specific day"}
              </Typography>
            </Box>

            {/* Interval Configuration */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
              How often?
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ mb: 1 }}>Every</Typography>
              <TextField
                type="number"
                size="small"
                value={intervalCount}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty input for better UX when typing
                  if (value === "") {
                    setIntervalCount("");
                    setIntervalCountError("Must be at least 1");
                  } else {
                    const num = parseInt(value);
                    if (num < 1) {
                      setIntervalCount(num);
                      setIntervalCountError("Must be at least 1");
                    } else if (num > 1000) {
                      setIntervalCount(num);
                      setIntervalCountError("Maximum interval is 1000");
                    } else {
                      setIntervalCount(num);
                      setIntervalCountError("");
                    }
                  }
                }}
                error={!!intervalCountError}
                sx={{ width: 80 }}
                inputProps={{ min: 1, max: 1000 }}
              />
              <TextField
                select
                size="small"
                value={intervalDateType}
                onChange={(e) => setIntervalDateType(e.target.value)}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="day">
                  {pluralise("Day", intervalCount)}
                </MenuItem>
                <MenuItem value="week">
                  {pluralise("Week", intervalCount)}
                </MenuItem>
                <MenuItem value="month">
                  {pluralise("Month", intervalCount)}
                </MenuItem>
              </TextField>
            </Box>
            {intervalCountError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5, mb: 2 }}
              >
                {intervalCountError}
              </Typography>
            )}
            {!intervalCountError && <Box sx={{ mb: 3 }} />}

            {/* Day of week selector for fixed schedule weekly tasks */}
            {recurrenceType === "fixed_schedule" &&
              intervalDateType === "week" && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    On which day?
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {DAYS_OF_WEEK.map((day) => (
                      <Chip
                        key={day.value}
                        label={day.label.slice(0, 3)}
                        onClick={() => setSelectedDayOfWeek(day.value)}
                        sx={{
                          minWidth: 56,
                          bgcolor:
                            selectedDayOfWeek === day.value
                              ? "#197b68"
                              : "grey.200",
                          color:
                            selectedDayOfWeek === day.value
                              ? "white !important"
                              : "inherit",
                          fontWeight:
                            selectedDayOfWeek === day.value ? "bold" : "normal",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor:
                              selectedDayOfWeek === day.value
                                ? "#197b68"
                                : "grey.300",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

            {/* Day of month selector for fixed schedule monthly tasks */}
            {recurrenceType === "fixed_schedule" &&
              intervalDateType === "month" && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    On which day of the month?
                  </Typography>

                  <RadioGroup
                    value={monthScheduleMode}
                    onChange={(e) => setMonthScheduleMode(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    <FormControlLabel
                      value="date"
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: "#197b68",
                            "&.Mui-checked": { color: "#197b68" },
                          }}
                        />
                      }
                      label="On a specific date"
                      sx={{ "& .MuiFormControlLabel-label": { mb: 0 } }}
                    />
                    {monthScheduleMode === "date" && (
                      <Box sx={{ ml: 4, mb: 2 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                            (day) => (
                              <Chip
                                key={day}
                                label={day}
                                onClick={() =>
                                  setSelectedDate(dayjs().month(0).date(day))
                                }
                                sx={{
                                  minWidth: 40,
                                  bgcolor:
                                    selectedDate.date() === day
                                      ? "#197b68"
                                      : "grey.200",
                                  color:
                                    selectedDate.date() === day
                                      ? "white !important"
                                      : "inherit",
                                  fontWeight:
                                    selectedDate.date() === day
                                      ? "bold"
                                      : "normal",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    bgcolor:
                                      selectedDate.date() === day
                                        ? "#197b68"
                                        : "grey.300",
                                  },
                                }}
                              />
                            ),
                          )}
                        </Box>
                        {selectedDate.date() >= 29 && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: "rgba(255, 166, 0, 0.1)",
                              border: "1px solid rgba(255, 166, 0, 0.3)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              💡 For months with fewer days, the task will recur
                              on the last day of that month.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    <FormControlLabel
                      value="weekday"
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: "#197b68",
                            "&.Mui-checked": { color: "#197b68" },
                          }}
                        />
                      }
                      label="On a specific weekday"
                      sx={{ "& .MuiFormControlLabel-label": { mb: 0 } }}
                    />
                    {monthScheduleMode === "weekday" && (
                      <Box sx={{ ml: 4 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <TextField
                            select
                            size="small"
                            value={weekdayOccurrence}
                            onChange={(e) =>
                              setWeekdayOccurrence(Number(e.target.value))
                            }
                            sx={{ minWidth: 100 }}
                          >
                            {WEEKDAY_OCCURRENCES.map((occ) => (
                              <MenuItem key={occ.value} value={occ.value}>
                                {occ.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            value={selectedMonthWeekday}
                            onChange={(e) =>
                              setSelectedMonthWeekday(Number(e.target.value))
                            }
                            sx={{ minWidth: 120 }}
                          >
                            {DAYS_OF_WEEK.map((day) => (
                              <MenuItem key={day.value} value={day.value}>
                                {day.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1, fontStyle: "italic" }}
                        >
                          e.g., "Pay rent on the first Friday of every month"
                        </Typography>
                      </Box>
                    )}
                  </RadioGroup>
                </Box>
              )}

            {/* Summary */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "grey.100",
                border: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Task will be re-added to your list:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircle sx={{ color: "#197b68", fontSize: 28 }} />
                <Typography fontWeight="bold" sx={{ mb: "0 !important" }}>
                  {summaryText}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "grey.200",
          justifyContent: "space-between",
        }}
      >
        <Link
          component={RouterLink}
          to="/about/features/tasks/recurring-tasks"
          variant="body2"
          sx={{
            color: "#197b68",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          What are recurring tasks?
        </Link>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={step > 0 ? <ArrowBack /> : null}
            onClick={handleBack}
            sx={{ color: "grey.700" }}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {step === 1 && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                !!intervalCountError || !intervalCount || intervalCount < 1
              }
              sx={{
                bgcolor: "#197b68",
                "&:hover": { bgcolor: "#004a3c" },
                "&.Mui-disabled": {
                  bgcolor: "grey.400",
                  color: "grey.600",
                },
              }}
            >
              Save
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RecurrenceDialog;
